import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SelectField } from '@platform/ui-kit';
import { authFetch } from '../compat/api';
import HelpPanel from '../compat/HelpPanel';
import type { Help } from '../compat/helpTypes';
import { useDispositionPalette } from '../dispositionPalette';
import { GroupMultiSelect } from '../workspace/GroupMultiSelect';
import { GANTT_PHASE_FILL, PHASES, fmtMDY, phaseLabel, type PhaseKey } from '../compat/roadmapPhases';
import { Bubbles, Donut, Timeline, TipRows, useTip, type Tip } from './charts';
import { scaleColor, type Scale } from './scale';
import './ExecutiveDashboard.css';
import './ExecutiveDashboard.extra.css';

/**
 * Program Planning — Executive Dashboard.
 *
 * NEW WORK, commissioned by the owner 2026-11-04: it replaces the ported apr_template dashboard
 * and is built on the APR Executive Dashboard's design (pages/apr/executive), which the owner
 * named as the reference — "use the same UI design principles". Its chart kit (charts.tsx), its
 * magnitude ramp (scale.ts) and its stylesheet are COPIED from there with the prefix renamed
 * (exd2 → ppx); products never import from one another's folders (§1.7).
 *
 * The design principles carried over:
 *   - a fixed head (title, scope toolbar, tabs) and only the data below it scrolls; the height
 *     is MEASURED through a callback ref, because `height: 100%` resolves against nothing here;
 *   - one read, one row per (integration group, application); every aggregate is computed here,
 *     so a KPI never disagrees with a chart and every filter click is immediate;
 *   - a rail of cards (health, the cross-filters, what comes next) beside KPI cards and tabs;
 *   - ONE "Circles | Bars" switch for every chart, remembered per browser;
 *   - chart marks coloured by MAGNITUDE on the four-step ramp; a disposition is always a filled
 *     chip in the Disposition Matrix colour (§1.9.3), roadmap phases in the Gantt's colours;
 *   - the ID leads, the description follows; every mark has a tooltip; a mark that names a
 *     filter value cross-filters the screen.
 *
 * The tabs follow the data Program Planning holds: the roadmap, what it costs to build
 * (Disposition Cost Analysis) and what it returns (Benefit Realization). The money figures use
 * those screens' own formulas, so the dashboard and the screens agree.
 */

interface PhaseDates { start: string | null; end: string | null }
interface Benefit {
  tco: number | null; future_run_rate: number | null; disposition_cost: number | null;
  planned_project_cost: number | null; lease_buyback: number | null;
  license_renegotiation: boolean; renegotiated_license_cost: number | null; license_savings: number | null;
}
interface Row {
  group_id: string; application_id: string; name: string | null; disposition: string | null;
  status: string | null; phases: Record<PhaseKey, PhaseDates>; constraint_notes: string | null;
  effort_days: number | null;
  cost: { assessment: number | null; design: number | null; migration: number | null; testing: number | null; golive: number | null; total: number | null };
  benefit: Benefit | null;
}
interface Group {
  group_id: string; group_name: string | null; earliest_start: string | null; approved: boolean; confirmed_at: string | null;
}
interface CostLine { group_id: string; application_id: string; phase: string; role: string | null; location: string | null; cost: number }
interface Dep { source_group: string; target_group: string; source_application_id: string; target_application_id: string; dependency_type: string; source_phase_name: string | null }

type Tab = 'exec' | 'road' | 'dca' | 'br';
type Mode = 'bars' | 'circles';
const MODE_KEY = 'program_planning.executive-dashboard.mode';
function readMode(): Mode {
  try { return localStorage.getItem(MODE_KEY) === 'bars' ? 'bars' : 'circles'; } catch { return 'circles'; }
}

const NOT_SET = 'Not set';
/** deployment_status, in the order a roadmap moves through it. */
const STATUSES = [NOT_SET, 'Approved', 'In Development', 'Deployed'];
const STATUS_COLOUR: Record<string, string> = {
  [NOT_SET]: 'var(--ix-line)', Approved: 'var(--ix-accent)', 'In Development': '#e0a030', Deployed: 'var(--ix-good)',
};
/** Disposition Cost Analysis' phase keys, in phase order. */
const COST_PHASES: [PhaseKey, string][] = [
  ['assessment', 'Assessment_Planning'], ['design', 'Design_Architecture'], ['migration', 'Migration_Execution'],
  ['testing', 'Testing_Validation'], ['golive', 'GoLive_Optimization'],
];

const n = (v: number | null | undefined) => (typeof v === 'number' && isFinite(v) ? v : 0);
const money = (v: number) => {
  const a = Math.abs(v), s = v < 0 ? '−' : '';
  return a >= 1e6 ? `${s}$${(a / 1e6).toFixed(a >= 1e7 ? 0 : 1)}M` : a >= 1e3 ? `${s}$${Math.round(a / 1e3)}k` : `${s}$${Math.round(a)}`;
};
const moneyFull = (v: number) => `${v < 0 ? '−' : ''}$${Math.round(Math.abs(v)).toLocaleString()}`;
const pctOf = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)}%` : '—');
const sum = <T,>(rows: T[], f: (r: T) => number) => rows.reduce((t, r) => t + f(r), 0);
const keyOf = (r: { group_id: string; application_id: string }) => `${r.group_id}|${r.application_id}`;
const statusOf = (r: Row) => r.status || NOT_SET;

/* ── Benefit Realization's own formulas (screens/BenefitRealization.tsx) ─────────────────── */
/** Annual saving: current TCO − future run rate, or the licence saving on a renegotiation. */
const annualSavings = (b: Benefit) =>
  b.license_renegotiation && n(b.renegotiated_license_cost) > 0 ? n(b.license_savings) : n(b.tco) - n(b.future_run_rate);
/** One-time cost: disposition + planned project − lease buyback. */
const oneTime = (b: Benefit) => n(b.disposition_cost) + n(b.planned_project_cost);
/** Net savings over n years: n × annual − disposition − project + lease buyback. */
const netSavings = (b: Benefit, years: number) => annualSavings(b) * years - n(b.disposition_cost) - n(b.planned_project_cost) + n(b.lease_buyback);
/** ROI, the SPN formula: n × annual savings ÷ landed cost (disposition + project) × 100. */
const roiOf = (annual: number, landed: number, years: number) => (landed > 0 ? ((annual * years) / landed) * 100 : 0);
/** Break-even in months: (disposition + project − lease buyback) ÷ annual saving × 12. */
const breakEven = (b: Benefit) => {
  const a = annualSavings(b);
  return a > 0 ? Math.max(0, ((oneTime(b) - n(b.lease_buyback)) / a) * 12) : null;
};
/** Benefit Realization lists only records that carry a disposition cost (owner ruling 2026-11-04). */
const inBenefit = (r: Row) => !!r.benefit && n(r.benefit.disposition_cost) > 0;

/** "Nov 2026" — a KPI-sized date. */
const monthYear = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
const firstStart = (r: Row) => r.phases.assessment.start;
const lastEnd = (r: Row) => r.phases.golive.end;
const goLive = (r: Row) => r.phases.golive.start;

const DashHelp: Help = {
  title: 'Executive Dashboard',
  icon: '📊',
  purpose: 'The programme in one view — the roadmap, what it costs to build and what it returns — for every integration grouping, every figure traceable to the application it came from.',
  doHere: [
    'Scope the screen with <b>Integration Grouping</b>, <b>Disposition</b> and <b>Status</b>; Ctrl-click picks several groupings.',
    'Click a disposition or status in the left rail, or a ring segment or circle, to scope the screen to it. Click again to remove it.',
    'Switch every chart between <b>Circles</b> and <b>Bars</b> with the switch beside the tabs.',
    'Hover any mark for its figures.',
  ],
  fits: 'Executive Dashboard',
  firstTime: 'The dashboard reads what the other screens store: calculate a roadmap on Roadmap Schedule, costs on Disposition Cost Analysis, and benefits on Benefit Realization.',
  sections: [
    {
      type: 'defs',
      label: 'Figures',
      items: [
        { term: 'Build cost', color: '#0b5d8f', def: 'Disposition Cost Analysis: days × FTE × daily rate per phase and role.' },
        { term: 'Annual savings', color: '#1c7a55', def: 'Benefit Realization: current TCO − future run rate (or the licence saving on a renegotiation).' },
        { term: 'Net savings (n years)', color: '#1c7a55', def: 'n × annual savings − disposition cost − planned project cost + lease buyback.' },
        { term: 'ROI', color: '#b4622a', def: 'n × annual savings ÷ (disposition cost + planned project cost) — the SPN formula.' },
      ],
    },
  ],
};

export default function ExecutiveDashboard() {
  const palette = useDispositionPalette();
  const [rows, setRows] = useState<Row[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [lines, setLines] = useState<CostLine[]>([]);
  const [deps, setDeps] = useState<Dep[]>([]);
  const [asOf, setAsOf] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tab, setTab] = useState<Tab>('exec');
  const [pickedGroups, setPickedGroups] = useState<string[]>([]);
  const [pickedDisps, setPickedDisps] = useState<string[]>([]);
  const [pickedStatus, setPickedStatus] = useState<string[]>([]);
  const [mode, setModeState] = useState<Mode>(readMode);
  const setMode = (m: Mode) => {
    setModeState(m);
    try { localStorage.setItem(MODE_KEY, m); } catch { /* private window — the choice just does not persist */ }
  };
  const tip = useTip();

  /* The head, scope bar, KPIs and tabs never scroll — only the data. Height MEASURED through a
     callback ref (the screen mounts after a loading gate); see the APR dashboard for why. */
  const [boxH, setBoxH] = useState<number | null>(null);
  const cleanup = useRef<(() => void) | null>(null);
  const rootRef = useCallback((node: HTMLDivElement | null) => {
    cleanup.current?.();
    cleanup.current = null;
    if (!node) return;
    const main = node.closest('main');
    const measure = () => {
      const top = node.getBoundingClientRect().top;
      const padBottom = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      setBoxH(Math.max(360, Math.round(window.innerHeight - top - padBottom)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    window.addEventListener('resize', measure);
    cleanup.current = () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, []);
  useEffect(() => () => cleanup.current?.(), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch('/api/executive-dashboard');
      const d = await r.json();
      if (!d.success) throw new Error(d.error ?? `HTTP ${r.status}`);
      setRows(d.applications ?? []);
      setGroups(d.groups ?? []);
      setLines(d.cost_lines ?? []);
      setDeps(d.dependencies ?? []);
      setAsOf(d.as_of ?? '');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const dispositions = useMemo(() => {
    const seen = Array.from(new Set(rows.map((r) => (r.disposition ?? '').trim()).filter(Boolean))).sort();
    if (rows.some((r) => !(r.disposition ?? '').trim())) seen.push(NOT_SET);
    return seen;
  }, [rows]);
  const dispOf = (r: Row) => (r.disposition ?? '').trim() || NOT_SET;

  const view = useMemo(() => rows.filter((r) =>
    (!pickedGroups.length || pickedGroups.includes(r.group_id)) &&
    (!pickedDisps.length || pickedDisps.includes(dispOf(r))) &&
    (!pickedStatus.length || pickedStatus.includes(statusOf(r)))), [rows, pickedGroups, pickedDisps, pickedStatus]);
  const inView = useMemo(() => new Set(view.map(keyOf)), [view]);
  const viewLines = useMemo(() => lines.filter((l) => inView.has(keyOf(l))), [lines, inView]);
  const viewGroups = useMemo(() => groups.filter((g) => view.some((r) => r.group_id === g.group_id)), [groups, view]);

  const totals = useMemo(() => {
    const ben = view.filter(inBenefit);
    const annual = sum(ben, (r) => annualSavings(r.benefit!));
    const landed = sum(ben, (r) => oneTime(r.benefit!));
    const starts = view.map(firstStart).filter(Boolean).sort() as string[];
    const ends = view.map(lastEnd).filter(Boolean).sort() as string[];
    return {
      build: sum(view, (r) => n(r.cost.total)),
      effort: sum(view, (r) => n(r.effort_days)),
      annual, landed,
      net: [1, 2, 3].map((y) => sum(ben, (r) => netSavings(r.benefit!, y))),
      roi3: roiOf(annual, landed, 3),
      benefitCount: ben.length,
      start: starts[0] ?? null, end: ends[ends.length - 1] ?? null,
      scheduled: view.filter((r) => firstStart(r) && lastEnd(r)).length,
      approvedish: view.filter((r) => ['Approved', 'In Development', 'Deployed'].includes(statusOf(r))).length,
      costed: view.filter((r) => n(r.cost.total) > 0).length,
    };
  }, [view]);

  const byDisposition = useMemo(() => dispositions.map((d) => {
    const rs = view.filter((r) => dispOf(r) === d);
    const ben = rs.filter(inBenefit);
    return { d, rows: rs, count: rs.length, build: sum(rs, (r) => n(r.cost.total)), annual: sum(ben, (r) => annualSavings(r.benefit!)) };
  }).filter((x) => x.count > 0), [dispositions, view]);

  const byStatus = useMemo(() => STATUSES.map((s) => ({ s, count: view.filter((r) => statusOf(r) === s).length })).filter((x) => x.count > 0), [view]);

  const colorOf = (d: string) => palette(d === NOT_SET ? '' : d);
  const flip = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const toggleDisp = (d: string) => setPickedDisps(flip(pickedDisps, d));
  const toggleStatus = (s: string) => setPickedStatus(flip(pickedStatus, s));
  const toggleGroup = (g: string) => setPickedGroups(flip(pickedGroups, g));

  // The heading stays while loading or on an error, so the screen always has its title.
  if (loading || error) {
    return (
      <div className="ppx ix" data-testid="pp-exec-dashboard">
        <div className="ix-head">
          <div className="ix-titlerow">
            <i className="material-icons ix-titleicon" aria-hidden="true">query_stats</i>
            <div><h1 className="ix-title">Executive Dashboard</h1></div>
          </div>
        </div>
        {error ? <div className="ppx-err">Could not load the dashboard: {error}</div> : <div className="ppx-empty">Loading the programme…</div>}
      </div>
    );
  }

  const unscoped = !rows.length;
  const pctN = (a: number, b: number) => (b ? (a / b) * 100 : 0);
  const gc = (v: number) => (v > 66 ? 'var(--ix-good)' : v > 33 ? 'var(--ix-warn)' : 'var(--ix-crit)');
  const today = asOf || new Date().toISOString().slice(0, 10);
  const nextGoLives = view.filter((r) => goLive(r) && goLive(r)! >= today).sort((a, b) => goLive(a)!.localeCompare(goLive(b)!)).slice(0, 5);
  const topN = (f: (r: Row) => number, k = 8) => view.map(f).sort((a, b) => b - a).slice(0, k);
  const ctx: Ctx = { view, groups: viewGroups, allGroups: groups, lines: viewLines, deps, totals, byDisposition, colorOf, dispOf, mode, tip,
    pickedDisps, toggleDisp, pickedGroups, toggleGroup, scaleColor, today };

  return (
    <div className="ppx ix" ref={rootRef} style={boxH ? { height: boxH } : undefined} data-testid="pp-exec-dashboard">
      <div className="ix-head">
        <div className="ix-titlerow">
          <i className="material-icons ix-titleicon" aria-hidden="true">query_stats</i>
          <div>
            <h1 className="ix-title">Executive Dashboard</h1>
            <p className="ix-sub">The roadmap, what it costs to build and what it returns — every figure traceable to the application it came from.</p>
            {/* The as-of line sits here, not in the top-right corner the APR layout uses: every Program
                Planning screen has its fixed "How this works" button there. */}
            <p className="ix-sub" data-testid="exec-asof">
              <b>{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</b>
              {` · ${new Set(rows.map((r) => r.application_id)).size} applications in ${groups.length} integration grouping${groups.length === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>

        <div className="ix-toolbar">
          <GroupMultiSelect label="Integration Grouping" groups={groups.map((g) => ({ id: g.group_id, description: g.group_name }))}
                            value={pickedGroups} onChange={setPickedGroups} testId="exec-group-picker" />
          <SelectField label="Disposition" width="md" multiple allLabel="All dispositions" placeholder="— Select a Disposition —"
                       value={pickedDisps} onChange={setPickedDisps}
                       options={dispositions.map((d) => ({ value: d, label: d }))} />
          <SelectField label="Status" width="md" multiple allLabel="All statuses" placeholder="— Select a Status —"
                       value={pickedStatus} onChange={setPickedStatus}
                       options={STATUSES.filter((s) => rows.some((r) => statusOf(r) === s)).map((s) => ({ value: s, label: s }))} />
          <div className="ix-tools">
            <button type="button" className="ppx-reset" onClick={() => { setPickedGroups([]); setPickedDisps([]); setPickedStatus([]); }}>Reset</button>
            <button type="button" className="ppx-reset" onClick={() => void load()}>↻ Refresh</button>
            <HelpPanel help={DashHelp} />
          </div>
        </div>

        {!unscoped ? (
          <div className="ix-tabs" role="tablist">
            {([['exec', 'Executive Summary'], ['road', 'Roadmap'], ['dca', 'Disposition Cost Analysis'], ['br', 'Benefit Realization']] as [Tab, string][]).map(([k, t]) => (
              <button key={k} type="button" role="tab" className="ix-tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`exec-tab-${k}`}>{t}</button>
            ))}
            <div className="ppx-mode" role="group" aria-label="Chart style">
              <button type="button" aria-pressed={mode === 'circles'} onClick={() => setMode('circles')} title="Circles">
                <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                  <circle cx="5" cy="8" r="4.5" fill="currentColor" /><circle cx="10.5" cy="4.5" r="3" fill="currentColor" opacity=".55" />
                </svg>
                Circles
              </button>
              <button type="button" aria-pressed={mode === 'bars'} onClick={() => setMode('bars')} title="Bars">
                <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                  <rect x="1" y="2" width="12" height="3" rx="1" fill="currentColor" />
                  <rect x="1" y="7" width="8" height="3" rx="1" fill="currentColor" opacity=".55" />
                </svg>
                Bars
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="ix-body">
        {unscoped ? (
          <div className="ppx-empty">
            <h3>No integration groupings yet</h3>
            <p>Create integration groupings and add their applications on <strong>Grouping &amp; Scope → Integration Groupings</strong> — this dashboard reads the roadmap, costs and benefits planned for them.</p>
          </div>
        ) : (
          <div className="ix-layout">
            <aside className="ix-rail">
              <div className="ix-card">
                <div className="ix-card-hd"><div className="eyebrow">Programme health</div></div>
                <div className="ix-card-bd">
                  <div className="ix-gauges">
                    <Gauge value={pctN(totals.scheduled, view.length)} label="Scheduled" colour={gc(pctN(totals.scheduled, view.length))} />
                    <Gauge value={pctN(totals.approvedish, view.length)} label="Approved" colour={gc(pctN(totals.approvedish, view.length))} />
                    <Gauge value={pctN(totals.costed, view.length)} label="Costed" colour={gc(pctN(totals.costed, view.length))} />
                  </div>
                  <div className="ix-ft">
                    <div className="ix-ft-row"><span>Applications</span><b>{view.length}</b></div>
                    <div className="ix-ft-row"><span>Integration groupings</span><b>{viewGroups.length}</b></div>
                    <div className="ix-ft-row"><span>Build cost</span><b>{money(totals.build)}</b></div>
                    <div className="ix-ft-row"><span>Annual savings</span><b>{money(totals.annual)}</b></div>
                    <div className="ix-ft-row"><span>Net savings, 3 years</span><b>{money(totals.net[2]!)}</b></div>
                  </div>
                </div>
              </div>

              <div className="ix-card">
                <div className="ix-card-hd"><div className="eyebrow">Status</div>
                  <span className="ix-card-meta">{totals.approvedish} of {view.length} approved</span>
                </div>
                <div className="ix-card-bd">
                  <div className="ix-stack">
                    {byStatus.map((x) => <i key={x.s} style={{ width: `${pctN(x.count, view.length)}%`, background: STATUS_COLOUR[x.s] }} />)}
                  </div>
                  <div className="ix-ft" style={{ borderTop: 0, marginTop: 9 }}>
                    {byStatus.map((x) => {
                      const on = pickedStatus.includes(x.s);
                      return (
                        <button key={x.s} type="button" data-testid="exec-status-row"
                          className={`ix-ft-row ix-ft-btn${pickedStatus.length && !on ? ' dim' : ''}${on ? ' on' : ''}`}
                          aria-pressed={on} title={on ? 'Remove from the selection' : 'Scope the screen to this status'}
                          onClick={() => toggleStatus(x.s)}>
                          <span><i className="ix-sw" style={{ background: STATUS_COLOUR[x.s], marginRight: 7 }} />{x.s}</span>
                          <b>{x.count}</b>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="ix-card">
                <div className="ix-card-hd"><div className="eyebrow">Disposition</div>
                  <span className="ix-card-meta">{view.length} applications</span>
                </div>
                <div className="ix-card-bd">
                  <div className="ix-stack">
                    {byDisposition.map((x) => <i key={x.d} style={{ width: `${pctN(x.count, view.length)}%`, background: colorOf(x.d).base }} />)}
                  </div>
                  <div className="ix-ft" style={{ borderTop: 0, marginTop: 9 }}>
                    {byDisposition.map((x) => {
                      const on = pickedDisps.includes(x.d);
                      return (
                        <button key={x.d} type="button" data-testid="exec-disp-row"
                          className={`ix-ft-row ix-ft-btn${pickedDisps.length && !on ? ' dim' : ''}${on ? ' on' : ''}`}
                          aria-pressed={on} title={on ? 'Remove from the selection' : 'Scope the screen to this disposition'}
                          onClick={() => toggleDisp(x.d)}>
                          <span><i className="ix-sw" style={{ background: colorOf(x.d).base, marginRight: 7 }} />{x.d}</span>
                          <b>{x.count}</b>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="ix-card">
                <div className="ix-card-hd"><div className="eyebrow">Next go-lives</div></div>
                <div className="ix-card-bd">
                  {nextGoLives.length ? (
                    <div className="ix-ft" style={{ borderTop: 0, marginTop: 0 }}>
                      {nextGoLives.map((r) => (
                        <div className="ix-ft-row" key={keyOf(r)}
                             onPointerEnter={(e) => tip.show(e, appTip(r, ctx))} onPointerMove={tip.move} onPointerLeave={tip.hide}>
                          <span className="ix-clip"><b className="mono">{r.group_id}</b> · {r.application_id}</span>
                          <b>{fmtMDY(goLive(r))}</b>
                        </div>
                      ))}
                    </div>
                  ) : <p className="ix-note" style={{ margin: 0 }}>No go-live ahead in this selection.</p>}
                </div>
              </div>
            </aside>

            <div className="ix-main">
              <div className="ix-kpis" data-testid="exec-kpis">
                <Kpi label="Applications" value={String(view.length)}
                     sub={`${viewGroups.length} integration grouping${viewGroups.length === 1 ? '' : 's'}`}
                     graphic={<MiniBars values={viewGroups.map((g) => view.filter((r) => r.group_id === g.group_id).length)} colour="var(--ix-accent)" />} />
                <Kpi label="Programme" value={totals.start && totals.end ? `${monthYear(totals.start)} – ${monthYear(totals.end)}` : '—'}
                     sub={totals.start && totals.end ? `${Math.round((new Date(totals.end).getTime() - new Date(totals.start).getTime()) / (86400000 * 30.44))} months, first start to last go-live` : 'no roadmap calculated yet'} />
                <Kpi label="Build cost" value={money(totals.build)} sub={`${Math.round(totals.effort).toLocaleString()} planned effort days`}
                     graphic={<MiniBars values={topN((r) => n(r.cost.total))} colour="var(--ix-s3)" />} />
                <Kpi label="Annual savings" value={money(totals.annual)} sub={`${totals.benefitCount} application${totals.benefitCount === 1 ? '' : 's'} with a business case`}
                     graphic={<MiniBars values={topN((r) => (inBenefit(r) ? annualSavings(r.benefit!) : 0))} colour="var(--ix-good)" />} />
                <Kpi label="Net savings, 3 years" value={money(totals.net[2]!)}
                     delta={totals.net[2]! < 0 ? 'not yet paid back' : undefined} deltaCol="var(--ix-crit)"
                     sub={`1 yr ${money(totals.net[0]!)} · 2 yrs ${money(totals.net[1]!)}`} />
                <Kpi label="ROI, 3 years" value={totals.landed > 0 ? `${Math.round(totals.roi3)}%` : '—'}
                     sub={`on ${money(totals.landed)} one-time cost`} />
              </div>

              {tab === 'exec' && <ExecTab c={ctx} />}
              {tab === 'road' && <RoadTab c={ctx} />}
              {tab === 'dca' && <CostTab c={ctx} />}
              {tab === 'br' && <BenefitTab c={ctx} />}
            </div>
          </div>
        )}
      </div>
      {tip.node}
    </div>
  );
}

/* ── shared pieces ─────────────────────────────────────────────────────────────────────── */

type Colored = (d: string) => { base: string; ink: string; tint: string; onBase: string };
type Bucket = { d: string; rows: Row[]; count: number; build: number; annual: number };
interface Ctx {
  view: Row[]; groups: Group[]; allGroups: Group[]; lines: CostLine[]; deps: Dep[];
  totals: { build: number; effort: number; annual: number; landed: number; net: number[]; roi3: number; benefitCount: number; start: string | null; end: string | null };
  byDisposition: Bucket[]; colorOf: Colored; dispOf: (r: Row) => string; mode: Mode; tip: Tip;
  pickedDisps: string[]; toggleDisp: (d: string) => void; pickedGroups: string[]; toggleGroup: (g: string) => void;
  scaleColor: Scale; today: string;
}

/* Gauge, MiniBars, Kpi, Card are COPIED from pages/apr/executive/ExecutiveDashboard.tsx (§1.7). */
function Gauge({ value, label, colour }: { value: number; label: string; colour: string }) {
  const size = 62, r = size / 2 - 7, c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, value));
  return (
    <div className="ix-gauge">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label={`${label} ${Math.round(v)}%`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5eaef" strokeWidth={7} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={colour} strokeWidth={7}
          strokeLinecap="round" strokeDasharray={`${(v / 100) * c} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        <text x={size / 2} y={size / 2 + 4} textAnchor="middle"
          style={{ font: "700 12.5px 'IBM Plex Mono',monospace", fill: '#14202a' }}>{Math.round(v)}%</text>
      </svg>
      <span className="lbl">{label}</span>
    </div>
  );
}

function MiniBars({ values, colour }: { values: number[]; colour: string }) {
  if (!values.length || !values.some((v) => v > 0)) return null;
  const W = 118, H = 26, gap = 3;
  const bw = (W - gap * (values.length - 1)) / values.length;
  const max = Math.max(...values) || 1;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-hidden="true">
      {values.map((v, i) => {
        const h = Math.max(v > 0 ? 2 : 0, (v / max) * (H - 3));
        return <rect key={i} x={i * (bw + gap)} y={H - h} width={bw} height={h} rx={1.5} fill={colour} fillOpacity={v > 0 ? 0.9 : 0.18} />;
      })}
    </svg>
  );
}

function Kpi({ label, value, delta, deltaCol, sub, graphic }: {
  label: string; value: string; delta?: string; deltaCol?: string; sub: string; graphic?: React.ReactNode;
}) {
  return (
    <div className="ix-kpi">
      <div className="eyebrow">{label}</div>
      <div className="v"><b>{value}</b>{delta ? <span className="d" style={{ color: deltaCol }}>{delta}</span> : null}</div>
      {graphic ? <div className="ix-spark">{graphic}</div> : null}
      <small>{sub}</small>
    </div>
  );
}

function Card({ eyebrow, title, meta, sub, span, children, testId }: {
  eyebrow: string; title: string; meta?: string; sub?: string; span?: boolean; children: React.ReactNode; testId?: string;
}) {
  return (
    <section className={`ix-panel${span ? ' span' : ''}`} data-testid={testId}>
      <header>
        <div className="ix-panel-top">
          <div className="eyebrow">{eyebrow}</div>
          <span className="c-right">{meta ? <span className="c-meta">{meta}</span> : null}</span>
        </div>
        <h2>{title}</h2>
        {sub ? <p className="ix-note">{sub}</p> : null}
      </header>
      {children}
    </section>
  );
}

/** A disposition inside a tooltip or a cell: a FILLED chip in its matrix colour (§1.9.3). */
function dispChip(d: string, colorOf: Colored) {
  const c = colorOf(d);
  return <span className="t-disp" style={{ background: c.base, color: c.onBase }}>{d}</span>;
}

/** Group and application, ID first: "IG002 · APP-0544 — name". */
function appLabel(r: Row) {
  return <><b className="mono">{r.group_id}</b> · <span className="mono">{r.application_id}</span>{r.name ? <> — {r.name}</> : null}</>;
}

function appTip(r: Row, c: Ctx) {
  const b = inBenefit(r) ? r.benefit! : null;
  return (
    <TipRows
      swatch={c.colorOf(c.dispOf(r)).base}
      title={appLabel(r)}
      sub={[statusOf(r), firstStart(r) ? `${fmtMDY(firstStart(r))} – ${fmtMDY(lastEnd(r))}` : 'not scheduled'].join(' · ')}
      rows={[
        ['disposition', dispChip(c.dispOf(r), c.colorOf)],
        ['go-live', fmtMDY(goLive(r))],
        ['build cost', moneyFull(n(r.cost.total))],
        ...(b ? [['annual savings', moneyFull(annualSavings(b))], ['net, 3 years', moneyFull(netSavings(b, 3))]] as [string, string][] : []),
      ]}
    />
  );
}

/** One chart, drawn as bars or circles by the screen-wide switch. */
function Chart({ c, items, fmt, height = 290, onClick, picked }: {
  c: Ctx; items: { key: string; label: string; rowLabel?: React.ReactNode; value: number; tip: React.ReactNode }[];
  fmt: (v: number) => string; height?: number; onClick?: (k: string) => void; picked?: string[];
}) {
  const live = items.filter((x) => x.value > 0);
  const max = Math.max(1, ...live.map((x) => x.value));
  if (!live.length) return <p className="ix-note">Nothing to show in this selection.</p>;
  if (c.mode === 'bars') {
    return (
      <div className="ppx-rows">
        {live.map((x) => {
          const on = picked?.includes(x.key);
          return (
            <div key={x.key} className={`ppx-row${onClick ? ' click' : ''}${picked?.length && !on ? ' dim' : ''}`}
                 onClick={onClick ? () => { c.tip.hide(); onClick(x.key); } : undefined}
                 onPointerEnter={(e) => c.tip.show(e, x.tip)} onPointerMove={c.tip.move} onPointerLeave={c.tip.hide}>
              <span className="r-lab">{x.rowLabel ?? x.label}</span>
              <div className="r-bar"><div style={{ width: `${(x.value / max) * 100}%`, background: scaleColor(x.value, max).fill }} /></div>
              <span className="r-num">{fmt(x.value)}</span>
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <Bubbles tip={c.tip} height={height}
             items={live.map((x) => {
               const sc = scaleColor(x.value, max);
               return { key: x.key, label: x.label, sub: fmt(x.value), value: x.value, fill: sc.fill, ink: sc.ink, tip: x.tip };
             })} />
  );
}

/** Part-to-whole by disposition: a ring (circles) or a stacked bar (bars). Click scopes. */
function DispositionMix({ c, value, centre }: { c: Ctx; value: (x: Bucket) => number; centre: { top: string; bot: string } }) {
  const total = sum(c.byDisposition, value);
  const dispTip = (x: Bucket) => (
    <TipRows title={dispChip(x.d, c.colorOf)}
             rows={[['applications', x.count], ['build cost', moneyFull(x.build)], ['annual savings', moneyFull(x.annual)], ['share', pctOf(value(x), total)]]} />
  );
  if (total <= 0) return <p className="ix-note">Nothing to show in this selection.</p>;
  if (c.mode === 'bars') {
    return (
      <div className="ppx-mix">
        {c.byDisposition.map((x) => {
          const w = (Math.max(0, value(x)) / total) * 100;
          if (w <= 0) return null;
          const col = c.colorOf(x.d);
          return (
            <div key={x.d} className="ppx-mix-seg" style={{ width: `${w}%`, background: col.base, color: col.onBase }}
                 onClick={() => { c.tip.hide(); c.toggleDisp(x.d); }}
                 onPointerEnter={(e) => c.tip.show(e, dispTip(x))} onPointerMove={c.tip.move} onPointerLeave={c.tip.hide}>
              {w > 9 ? `${Math.round(w)}%` : ''}
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div className="ppx-ring-row">
      <Donut size={220} tip={c.tip} centre={centre}
             parts={c.byDisposition.map((x) => ({
               key: x.d, value: Math.max(0, value(x)), fill: c.colorOf(x.d).base, tip: dispTip(x),
               onClick: () => c.toggleDisp(x.d), selected: c.pickedDisps.includes(x.d),
               dimmed: c.pickedDisps.length > 0 && !c.pickedDisps.includes(x.d),
             }))} />
      <div className="ppx-key">
        {c.byDisposition.filter((x) => value(x) > 0).map((x) => (
          <button key={x.d} type="button" className={`ppx-key-row${c.pickedDisps.length && !c.pickedDisps.includes(x.d) ? ' dim' : ''}`}
                  aria-pressed={c.pickedDisps.includes(x.d)} onClick={() => { c.tip.hide(); c.toggleDisp(x.d); }}
                  onPointerEnter={(e) => c.tip.show(e, dispTip(x))} onPointerMove={c.tip.move} onPointerLeave={c.tip.hide}>
            <span className="dot" style={{ background: c.colorOf(x.d).base }} />
            <span className="k-name">{x.d}</span>
            <span className="k-val">{money(value(x))}</span>
            <span className="k-pct">{pctOf(value(x), total)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function groupTip(g: Group, rs: Row[]) {
  const ben = rs.filter(inBenefit);
  const starts = rs.map(firstStart).filter(Boolean).sort() as string[];
  const ends = rs.map(lastEnd).filter(Boolean).sort() as string[];
  return (
    <TipRows title={<><b className="mono">{g.group_id}</b> — {g.group_name}</>}
             sub={`${rs.length} application${rs.length === 1 ? '' : 's'} · ${g.approved ? 'roadmap approved' : 'roadmap not approved'}`}
             rows={[
               ['earliest start', fmtMDY(g.earliest_start)],
               ['roadmap', starts.length ? `${fmtMDY(starts[0])} – ${fmtMDY(ends[ends.length - 1])}` : 'not calculated'],
               ['build cost', moneyFull(sum(rs, (r) => n(r.cost.total)))],
               ['annual savings', moneyFull(sum(ben, (r) => annualSavings(r.benefit!)))],
             ]} />
  );
}

/* ── tabs ──────────────────────────────────────────────────────────────────────────────── */

function ExecTab({ c }: { c: Ctx }) {
  const t = c.totals;
  const ben = c.view.filter(inBenefit);
  const top = [...ben].sort((a, b) => netSavings(b.benefit!, 3) - netSavings(a.benefit!, 3)).slice(0, 6);
  const approved = c.groups.filter((g) => g.approved).length;
  return (
    <div className="ppx-grid">
      <Card eyebrow="Summary" title="The position" span testId="exec-lede">
        <p className="ppx-lede">
          <b>{c.view.length}</b> applications across <b>{c.groups.length}</b> integration grouping{c.groups.length === 1 ? '' : 's'}
          {t.start && t.end ? <> run from <b>{fmtMDY(t.start)}</b> to a last go-live on <b>{fmtMDY(t.end)}</b></> : <> have no calculated roadmap yet</>}.
          {' '}Building it costs <b>{moneyFull(t.build)}</b>; once live it saves <b>{moneyFull(t.annual)}</b> a year —{' '}
          <b>{moneyFull(t.net[2]!)}</b> net over three years{t.landed > 0 ? <>, a three-year ROI of <b>{Math.round(t.roi3)}%</b></> : null}.
          {' '}<b>{approved}</b> of {c.groups.length} roadmap{c.groups.length === 1 ? ' is' : 's are'} approved.
        </p>
      </Card>

      <Card eyebrow="Where the money goes" title="Build cost by disposition" meta="share of build cost" span
            sub="How the cost of the programme divides across dispositions. Click a disposition to scope to it.">
        <DispositionMix c={c} value={(x) => x.build} centre={{ top: money(t.build), bot: 'build cost' }} />
      </Card>

      <Card eyebrow="Integration groupings" title="Build cost by grouping" meta={`${c.groups.length} groupings`}
            sub="Click a grouping to scope the screen to it.">
        <Chart c={c} fmt={money} onClick={c.toggleGroup} picked={c.pickedGroups}
               items={c.groups.map((g) => {
                 const rs = c.view.filter((r) => r.group_id === g.group_id);
                 return { key: g.group_id, label: g.group_id, rowLabel: <><b className="mono">{g.group_id}</b> {g.group_name}</>,
                          value: sum(rs, (r) => n(r.cost.total)), tip: groupTip(g, rs) };
               })} />
      </Card>

      <Card eyebrow="Return" title="Annual savings by grouping" meta="Benefit Realization">
        <Chart c={c} fmt={money} onClick={c.toggleGroup} picked={c.pickedGroups}
               items={c.groups.map((g) => {
                 const rs = c.view.filter((r) => r.group_id === g.group_id);
                 return { key: g.group_id, label: g.group_id, rowLabel: <><b className="mono">{g.group_id}</b> {g.group_name}</>,
                          value: sum(rs.filter(inBenefit), (r) => annualSavings(r.benefit!)), tip: groupTip(g, rs) };
               })} />
      </Card>

      <Card eyebrow="Biggest moves" title="The moves that matter" meta="by net savings over 3 years" span
            sub="The applications whose business case returns the most over three years, after their one-time costs.">
        {top.length ? (
          <div className="ppx-moves">
            {top.map((r, i) => {
              const v = netSavings(r.benefit!, 3);
              const sc = scaleColor(Math.max(0, v), Math.max(1, netSavings(top[0]!.benefit!, 3)));
              const col = c.colorOf(c.dispOf(r));
              return (
                <div key={keyOf(r)} className="ppx-move" style={{ borderLeft: `5px solid ${sc.fill}` }}
                     onPointerEnter={(e) => c.tip.show(e, appTip(r, c))} onPointerMove={c.tip.move} onPointerLeave={c.tip.hide}>
                  <div className="rank" style={{ color: sc.ink }}>{i + 1}</div>
                  <div>
                    <div className="m-ti">{appLabel(r)}{' '}
                      <span className="ppx-pill" style={{ background: col.base, color: col.onBase }}>{c.dispOf(r)}</span>
                    </div>
                    <div className="m-bd">{[statusOf(r), goLive(r) ? `go-live ${fmtMDY(goLive(r))}` : null, `build ${money(n(r.cost.total))}`, `${money(annualSavings(r.benefit!))} a year`].filter(Boolean).join(' · ')}</div>
                  </div>
                  <div className="m-val">{money(v)}<small>net, 3 years</small></div>
                </div>
              );
            })}
          </div>
        ) : <p className="ix-note">No application in this selection has a business case yet — see Benefit Realization.</p>}
      </Card>
    </div>
  );
}

function RoadTab({ c }: { c: Ctx }) {
  const scheduled = c.view.filter((r) => firstStart(r) && lastEnd(r));
  const lo = scheduled.map(firstStart).sort()[0];
  const hi = scheduled.map(lastEnd).sort().slice(-1)[0];
  const t0 = lo ? new Date(lo).getTime() : 0, t1 = hi ? new Date(hi).getTime() : 1;
  const pos = (d: string) => ((new Date(d).getTime() - t0) / Math.max(1, t1 - t0)) * 100;
  const maxBuild = Math.max(1, ...scheduled.map((r) => n(r.cost.total)));
  const cross = c.deps.filter((d) => d.source_group !== d.target_group &&
    (c.view.some((r) => r.group_id === d.target_group && r.application_id === d.target_application_id)));
  const rowOf = (g: string, a: string) => c.view.find((r) => r.group_id === g && r.application_id === a);
  return (
    <div className="ppx-grid">
      <Card eyebrow="Roadmap" title="Integration groupings over time" meta={lo && hi ? `${fmtMDY(lo)} – ${fmtMDY(hi)}` : 'not calculated'} span testId="exec-roadmap"
            sub="Each grouping's applications, phase by phase, in the Gantt's colours. Click a grouping to scope to it.">
        {scheduled.length ? (
          <div className="ppx-gantt">
            <div className="ppx-gantt-key">
              {PHASES.map((p) => <span key={p.key}><i style={{ background: GANTT_PHASE_FILL[p.name] }} />{phaseLabel(p.name)}</span>)}
            </div>
            {c.groups.map((g) => {
              const rs = scheduled.filter((r) => r.group_id === g.group_id);
              if (!rs.length) return null;
              const on = c.pickedGroups.includes(g.group_id);
              return (
                <div key={g.group_id} className={`ppx-gantt-grp${c.pickedGroups.length && !on ? ' dim' : ''}`}>
                  <button type="button" className="ppx-gantt-gh" aria-pressed={on} onClick={() => { c.tip.hide(); c.toggleGroup(g.group_id); }}
                          onPointerEnter={(e) => c.tip.show(e, groupTip(g, c.view.filter((r) => r.group_id === g.group_id)))} onPointerMove={c.tip.move} onPointerLeave={c.tip.hide}>
                    <b className="mono">{g.group_id}</b> {g.group_name}
                    {g.approved ? <span className="ppx-pill" style={{ background: 'var(--ix-accent-soft)', color: 'var(--ix-accent)' }}>Approved</span> : null}
                  </button>
                  {rs.map((r) => (
                    <div key={keyOf(r)} className="ppx-gantt-row" data-testid="exec-gantt-row"
                         onPointerEnter={(e) => c.tip.show(e, appTip(r, c))} onPointerMove={c.tip.move} onPointerLeave={c.tip.hide}>
                      <span className="g-lab"><span className="mono">{r.application_id}</span> {r.name}</span>
                      <div className="g-track">
                        {PHASES.map((p) => {
                          const s = r.phases[p.key].start, e = r.phases[p.key].end;
                          if (!s || !e) return null;
                          return <i key={p.key} style={{ left: `${pos(s)}%`, width: `${Math.max(0.4, pos(e) - pos(s))}%`, background: GANTT_PHASE_FILL[p.name] }} />;
                        })}
                      </div>
                      <span className="g-end">{fmtMDY(goLive(r))}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ) : <p className="ix-note">No roadmap is calculated for this selection — calculate it on Roadmap Schedule.</p>}
      </Card>

      <Card eyebrow="Go-lives" title="When applications go live" meta="sized and coloured by build cost" span
            sub="Each mark is an application's Go-Live and Support start.">
        <Timeline tip={c.tip}
                  items={scheduled.filter((r) => goLive(r)).map((r) => {
                    const sc = scaleColor(n(r.cost.total), maxBuild);
                    return { key: keyOf(r), date: goLive(r)!, value: Math.max(1, n(r.cost.total)), fill: sc.fill, label: r.application_id, tip: appTip(r, c) };
                  })} />
      </Card>

      <Card eyebrow="Approval" title="Status by grouping" meta={`${c.groups.filter((g) => g.approved).length} of ${c.groups.length} approved`}>
        <div className="ppx-rows">
          {c.groups.map((g) => {
            const rs = c.view.filter((r) => r.group_id === g.group_id);
            return (
              <div key={g.group_id} className="ppx-row" onPointerEnter={(e) => c.tip.show(e, groupTip(g, rs))} onPointerMove={c.tip.move} onPointerLeave={c.tip.hide}>
                <span className="r-lab"><b className="mono">{g.group_id}</b> {g.group_name}</span>
                <div className="ppx-stackbar">
                  {STATUSES.map((s) => {
                    const k = rs.filter((r) => statusOf(r) === s).length;
                    return k ? <i key={s} style={{ width: `${(k / rs.length) * 100}%`, background: STATUS_COLOUR[s] }} title={`${s}: ${k}`} /> : null;
                  })}
                </div>
                <span className="r-num">{rs.length} apps</span>
              </div>
            );
          })}
        </div>
        <div className="ppx-legend">{STATUSES.map((s) => <span key={s}><i style={{ background: STATUS_COLOUR[s] }} />{s}</span>)}</div>
      </Card>

      <Card eyebrow="Dependencies" title="Across integration groupings" meta={`${cross.length} dependenc${cross.length === 1 ? 'y' : 'ies'}`} testId="exec-cross">
        {cross.length ? (
          <div className="ppx-tbl">
            <table>
              <thead><tr><th>Waits</th><th>On</th><th>Rule</th><th>Starts</th></tr></thead>
              <tbody>
                {cross.map((d, i) => {
                  const tgt = rowOf(d.target_group, d.target_application_id);
                  return (
                    <tr key={i}>
                      <td><b className="mono">{d.target_group}</b> · <span className="mono">{d.target_application_id}</span></td>
                      <td><b className="mono">{d.source_group}</b> · <span className="mono">{d.source_application_id}</span></td>
                      <td>{d.dependency_type === 'finish_to_start' ? 'after Go-Live and Support' : `when it starts ${phaseLabel(d.source_phase_name)}`}</td>
                      <td title={tgt?.constraint_notes || ''}>{fmtMDY(tgt ? firstStart(tgt) : null)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <p className="ix-note">No application in this selection waits on another grouping.</p>}
      </Card>
    </div>
  );
}

function CostTab({ c }: { c: Ctx }) {
  const costed = c.view.filter((r) => n(r.cost.total) > 0);
  const byPhase = COST_PHASES.map(([k]) => ({ k, v: sum(costed, (r) => n(r.cost[k])) }));
  const roles = Array.from(new Set(c.lines.map((l) => l.role || '—')));
  const byRole = roles.map((role) => ({ role, v: sum(c.lines.filter((l) => (l.role || '—') === role), (l) => l.cost) })).sort((a, b) => b.v - a.v);
  const locs = Array.from(new Set(c.lines.map((l) => l.location || 'Not set')));
  const byLoc = locs.map((loc) => ({ loc, v: sum(c.lines.filter((l) => (l.location || 'Not set') === loc), (l) => l.cost) }));
  const locTotal = sum(byLoc, (x) => x.v);
  const top = [...costed].sort((a, b) => n(b.cost.total) - n(a.cost.total)).slice(0, 12);
  return (
    <div className="ppx-grid">
      <Card eyebrow="Cost by phase" title="Where the build cost goes" meta={`${money(c.totals.build)} total`} testId="exec-cost-phase"
            sub="Days × FTE × daily rate, per phase — Disposition Cost Analysis' figures.">
        <Chart c={c} fmt={money}
               items={byPhase.map(({ k, v }) => {
                 const p = PHASES.find((x) => x.key === k)!;
                 return { key: k, label: phaseLabel(p.name), value: v,
                          tip: <TipRows title={phaseLabel(p.name)} swatch={GANTT_PHASE_FILL[p.name]} rows={[['build cost', moneyFull(v)], ['of total', pctOf(v, c.totals.build)]]} /> };
               })} />
      </Card>

      <Card eyebrow="Cost by role" title="Who the money pays for" meta={`${byRole.length} roles`}>
        <Chart c={c} fmt={money}
               items={byRole.map((x) => ({ key: x.role, label: x.role, value: x.v,
                 tip: <TipRows title={x.role} rows={[['build cost', moneyFull(x.v)], ['of total', pctOf(x.v, locTotal)],
                   ['applications', new Set(c.lines.filter((l) => (l.role || '—') === x.role).map(keyOf)).size]]} /> }))} />
      </Card>

      <Card eyebrow="Delivery model" title="On-site and off-site" meta="share of build cost">
        {locTotal > 0 ? (
          <div className="ppx-ring-row">
            <Donut size={200} tip={c.tip} centre={{ top: money(locTotal), bot: 'by location' }}
                   parts={byLoc.map((x, i) => ({ key: x.loc, value: x.v, fill: ['var(--ix-s5)', 'var(--ix-s2)', 'var(--ix-s4)', 'var(--ix-s1)'][i % 4]!,
                     tip: <TipRows title={x.loc} rows={[['build cost', moneyFull(x.v)], ['share', pctOf(x.v, locTotal)]]} /> }))} />
            <div className="ppx-key">
              {byLoc.map((x, i) => (
                <div key={x.loc} className="ppx-key-row">
                  <span className="dot" style={{ background: ['var(--ix-s5)', 'var(--ix-s2)', 'var(--ix-s4)', 'var(--ix-s1)'][i % 4] }} />
                  <span className="k-name">{x.loc}</span><span className="k-val">{money(x.v)}</span><span className="k-pct">{pctOf(x.v, locTotal)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : <p className="ix-note">No resources are allocated in this selection.</p>}
      </Card>

      <Card eyebrow="Concentration" title="The most expensive applications" meta="coloured by build cost">
        <Chart c={c} fmt={money}
               items={top.map((r) => ({ key: keyOf(r), label: r.application_id, rowLabel: appLabel(r), value: n(r.cost.total), tip: appTip(r, c) }))} />
      </Card>

      <Card eyebrow="Register" title="Disposition cost by application" meta={`${costed.length} applications with a cost`} span testId="exec-cost-register">
        <div className="ppx-tbl">
          <table>
            <thead>
              <tr>
                <th>Application</th><th>Disposition</th>
                {PHASES.map((p) => <th key={p.key} className="num">{p.key === 'assessment' ? 'Assessment' : phaseLabel(p.name)}</th>)}
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {[...costed].sort((a, b) => a.group_id.localeCompare(b.group_id) || a.application_id.localeCompare(b.application_id)).map((r) => {
                const col = c.colorOf(c.dispOf(r));
                return (
                  <tr key={keyOf(r)}>
                    <td><b className="mono">{r.group_id}</b> · <b className="mono">{r.application_id}</b><br /><span className="muted">{r.name}</span></td>
                    <td><span className="ppx-pill" style={{ background: col.base, color: col.onBase }}>{c.dispOf(r)}</span></td>
                    {COST_PHASES.map(([k]) => <td key={k} className="num">{moneyFull(n(r.cost[k]))}</td>)}
                    <td className="num"><b>{moneyFull(n(r.cost.total))}</b></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function BenefitTab({ c }: { c: Ctx }) {
  const ben = c.view.filter(inBenefit);
  const annual = c.totals.annual, landed = c.totals.landed;
  const years = [1, 2, 3];
  const byGroup = c.groups.map((g) => {
    const rs = ben.filter((r) => r.group_id === g.group_id);
    return { g, rs, net: years.map((y) => sum(rs, (r) => netSavings(r.benefit!, y))), annual: sum(rs, (r) => annualSavings(r.benefit!)) };
  }).filter((x) => x.rs.length);
  const maxAbs = Math.max(1, ...byGroup.flatMap((x) => x.net.map(Math.abs)));
  const paybacks = ben.map((r) => breakEven(r.benefit!)).filter((v): v is number => v != null);
  const avgPayback = paybacks.length ? sum(paybacks, (v) => v) / paybacks.length : null;
  return (
    <div className="ppx-grid">
      <Card eyebrow="Business case" title="What the programme returns" span testId="exec-benefit-lede">
        <p className="ppx-lede">
          <b>{ben.length}</b> application{ben.length === 1 ? '' : 's'} with a business case save <b>{moneyFull(annual)}</b> a year against{' '}
          <b>{moneyFull(landed)}</b> of one-time cost. Net of that cost the programme returns <b>{moneyFull(c.totals.net[0]!)}</b> in year one,{' '}
          <b>{moneyFull(c.totals.net[1]!)}</b> over two years and <b>{moneyFull(c.totals.net[2]!)}</b> over three
          {landed > 0 ? <> — an ROI of <b>{Math.round(roiOf(annual, landed, 1))}%</b>, <b>{Math.round(roiOf(annual, landed, 2))}%</b> and <b>{Math.round(roiOf(annual, landed, 3))}%</b></> : null}.
          {avgPayback != null ? <> On average an application pays back in <b>{avgPayback.toFixed(1)} months</b>.</> : null}
        </p>
      </Card>

      <Card eyebrow="Where the savings come from" title="Annual savings by disposition" meta="share of annual savings" span
            sub="Click a disposition to scope to it.">
        <DispositionMix c={c} value={(x) => x.annual} centre={{ top: money(annual), bot: 'a year' }} />
      </Card>

      <Card eyebrow="Over time" title="Net savings by grouping, years 1 to 3" meta="after one-time costs" span testId="exec-net-years"
            sub="Green is ahead, red has not yet paid back. Hover a bar for the figures.">
        {byGroup.length ? (
          <div className="ppx-years">
            {byGroup.map((x) => (
              <div key={x.g.group_id} className="ppx-years-row"
                   onPointerEnter={(e) => c.tip.show(e, <TipRows title={<><b className="mono">{x.g.group_id}</b> — {x.g.group_name}</>}
                     rows={[['annual savings', moneyFull(x.annual)], ...years.map((y, i) => [`net, ${y} year${y > 1 ? 's' : ''}`, moneyFull(x.net[i]!)] as [string, string]),
                            ['ROI, 3 years', `${Math.round(roiOf(x.annual, sum(x.rs, (r) => oneTime(r.benefit!)), 3))}%`]]} />)}
                   onPointerMove={c.tip.move} onPointerLeave={c.tip.hide}>
                <span className="r-lab"><b className="mono">{x.g.group_id}</b> {x.g.group_name}</span>
                <div className="ppx-years-bars">
                  {x.net.map((v, i) => (
                    <div key={i} className="y">
                      <i style={{ width: `${(Math.abs(v) / maxAbs) * 100}%`, background: v < 0 ? 'var(--ix-crit)' : 'var(--ix-good)', opacity: 0.55 + i * 0.2 }} />
                      <span>Y{i + 1} {money(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : <p className="ix-note">No application in this selection has a business case yet.</p>}
      </Card>

      <Card eyebrow="Register" title="Benefit realization by application" meta={`${ben.length} applications`} span testId="exec-benefit-register"
            sub="Only applications with a disposition cost, as on Benefit Realization.">
        <div className="ppx-tbl">
          <table>
            <thead>
              <tr>
                <th>Application</th><th>Disposition</th>
                <th className="num">Current TCO</th><th className="num">Future run rate</th><th className="num">Annual savings</th>
                <th className="num">One-time cost</th><th className="num">Net, 1 yr</th><th className="num">Net, 2 yrs</th><th className="num">Net, 3 yrs</th>
                <th className="num">ROI, 3 yrs</th><th className="num">Break-even</th>
              </tr>
            </thead>
            <tbody>
              {[...ben].sort((a, b) => netSavings(b.benefit!, 3) - netSavings(a.benefit!, 3)).map((r) => {
                const b = r.benefit!;
                const col = c.colorOf(c.dispOf(r));
                const be = breakEven(b);
                return (
                  <tr key={keyOf(r)}>
                    <td><b className="mono">{r.group_id}</b> · <b className="mono">{r.application_id}</b><br /><span className="muted">{r.name}</span></td>
                    <td><span className="ppx-pill" style={{ background: col.base, color: col.onBase }}>{c.dispOf(r)}</span></td>
                    <td className="num">{moneyFull(n(b.tco))}</td>
                    <td className="num">{moneyFull(n(b.future_run_rate))}</td>
                    <td className="num"><b>{moneyFull(annualSavings(b))}</b></td>
                    <td className="num">{moneyFull(oneTime(b))}</td>
                    {years.map((y) => {
                      const v = netSavings(b, y);
                      return <td key={y} className="num" style={{ color: v < 0 ? 'var(--ix-crit)' : 'var(--ix-good)' }}>{moneyFull(v)}</td>;
                    })}
                    <td className="num">{oneTime(b) > 0 ? `${Math.round(roiOf(annualSavings(b), oneTime(b), 3))}%` : '—'}</td>
                    <td className="num">{be != null ? `${be.toFixed(1)} mo` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
